import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { topXFor, type GameState, type Round } from '@flugwettrennen/shared';
import { api, ApiError } from '../api';
import {
  RoundOptionsFields,
  type RoundOptionValues,
} from '../components/RoundOptionsFields';
import { dateTime } from '../lib/format';
import { goalLabel } from '../lib/goals';

function toFormValues(round: Round): RoundOptionValues {
  return {
    // Altrunden kennen nur das gemeinsame topX – das ist ihr wirksames X.
    topXIslands: topXFor(round.options, 'top_islands'),
    topXAirlines: topXFor(round.options, 'top_airlines'),
    forbiddenTopXAirports: round.options.forbiddenTopXAirports,
    uniqueAirportsOnly: round.options.uniqueAirportsOnly,
    failProbability: round.options.failProbability,
    minLayoverMinutes: round.options.minLayoverMinutes,
    changeCutoffMinutes: round.options.changeCutoffMinutes,
    customGoalText: round.options.customGoalText ?? '',
  };
}

/** Nachträgliches Bearbeiten, Zurücksetzen und Löschen einer Runde. */
export function RoundSettingsPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [round, setRound] = useState<Round | null>(null);
  const [state, setState] = useState<GameState | null>(null);
  const [name, setName] = useState('');
  const [options, setOptions] = useState<RoundOptionValues | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<'reset' | 'delete' | null>(null);

  useEffect(() => {
    api.rounds
      .get(id)
      .then((r) => {
        setRound(r.round);
        setState(r.state);
        setName(r.round.name);
        setOptions(toFormValues(r.round));
      })
      .catch(() => navigate('/', { replace: true }));
  }, [id, navigate]);

  function fail(err: unknown) {
    setError(
      err instanceof ApiError ? t([`errors.${err.code}`, 'errors.generic']) : t('errors.generic'),
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!round || !options) return;
    setError(null);
    setBusy(true);
    try {
      await api.rounds.update(round.id, {
        name,
        options: {
          ...options,
          customGoalText: round.goalTypes.includes('free') ? options.customGoalText || null : null,
        },
      });
      navigate(`/round/${round.id}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!round) return;
    setError(null);
    setBusy(true);
    try {
      await api.rounds.reset(round.id);
      navigate(`/round/${round.id}`);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  async function remove() {
    if (!round) return;
    setError(null);
    setBusy(true);
    try {
      await api.rounds.remove(round.id);
      navigate('/', { replace: true });
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  if (!round || !state || !options) return <div className="container page"><div className="spinner" /></div>;

  const backTo = round.status === 'finished' ? `/result/${round.id}` : `/round/${round.id}`;
  const hasLegs = state.legs.length > 0;

  return (
    <div className="container page">
      <div className="eyebrow">{t('settings.title')}</div>
      <h1>{round.name}</h1>
      <p className="muted" style={{ marginTop: -4 }}>{t('settings.subtitle')}</p>

      <form className="grid-2" onSubmit={save} style={{ alignItems: 'start', marginTop: 12 }}>
        <div className="card stack">
          <div className="field">
            <label htmlFor="nm">{t('create.name')}</label>
            <input
              id="nm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="settings-name"
            />
          </div>

          <div className="field">
            <label>{t('settings.fixed')}</label>
            <div className="muted mono" style={{ fontSize: '0.85rem', lineHeight: 1.8 }}>
              <div>{goalLabel(t, round.goalTypes)}</div>
              <div>{round.startAirportIata} → {round.destAirportIata}</div>
              <div>{dateTime(round.startDateTime)}</div>
              <div>{round.mode === 'sim' ? t('create.modeSim') : t('create.modeLive')}</div>
            </div>
            <span className="hint">{t('settings.fixedHint')}</span>
          </div>

          <div className="notice">{t('settings.challengeHint')}</div>
        </div>

        <div className="card stack">
          <div className="eyebrow">{t('create.options')}</div>

          <RoundOptionsFields
            values={options}
            onChange={(patch) => setOptions((o) => (o ? { ...o, ...patch } : o))}
            goalTypes={round.goalTypes}
            topXLocked={hasLegs}
          />

          {error && <div className="notice notice-error">{error}</div>}

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn-go" type="submit" disabled={busy} data-testid="settings-submit">
              {busy ? t('common.loading') : t('common.save')}
            </button>
            <Link className="btn btn-ghost" to={backTo}>{t('common.cancel')}</Link>
          </div>
        </div>
      </form>

      <div className="card stack" style={{ marginTop: 16 }}>
        <div className="eyebrow">{t('settings.dangerZone')}</div>

        <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <strong>{t('settings.reset')}</strong>
            <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.85rem' }}>
              {t('settings.resetHint')}
            </p>
          </div>
          {confirming === 'reset' ? (
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-danger"
                type="button"
                disabled={busy}
                onClick={() => void reset()}
                data-testid="round-reset-confirm"
              >
                {t('settings.resetConfirm')}
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => setConfirming(null)}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              className="btn"
              type="button"
              onClick={() => setConfirming('reset')}
              data-testid="round-reset"
            >
              {t('settings.reset')}
            </button>
          )}
        </div>

        <hr className="rule" />

        <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <strong>{t('settings.delete')}</strong>
            <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.85rem' }}>
              {t('settings.deleteHint')}
            </p>
          </div>
          {confirming === 'delete' ? (
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-danger"
                type="button"
                disabled={busy}
                onClick={() => void remove()}
                data-testid="round-delete-confirm"
              >
                {t('settings.deleteConfirm')}
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => setConfirming(null)}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              className="btn"
              type="button"
              onClick={() => setConfirming('delete')}
              data-testid="round-delete"
            >
              {t('settings.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
